/*
 * <cart-cross-sell> — the "Add matching pieces" section inside the cart drawer.
 *
 * Fetches the same live product recommendations the PDP "Explore more" section uses
 * (product recommendations route, with the first cart item's product_id baked into
 * data-url by the Liquid), extracts the server-rendered + filtered card list from the
 * response, injects it, then initializes a non-infinite Flickity slider with a draggable
 * scrollbar (ticket req 10). The slider + scrollbar logic mirrors complementary-slider-2.liquid.
 *
 * A new element instance is created every time the drawer re-renders (add/remove/quantity),
 * so connectedCallback re-fetches for the possibly-changed first cart item, and products that
 * were just added drop out automatically (server-side in-cart exclusion).
 */
(function () {
  "use strict";

  var FLICKITY_OPTIONS = {
    cellSelector: ".cart-cross-sell__item",
    cellAlign: "left",
    prevNextButtons: false,
    pageDots: false,
    freeScroll: false,
    dragThreshold: 3,
    wrapAround: false,
    groupCells: 1,
    resize: true,
    setGallerySize: true,
  };

  class CartCrossSell extends HTMLElement {
    constructor() {
      super();
      this.loaded = false;
      this.flickityRetries = 0;
      this.onCartDrawerMutation = this.onCartDrawerMutation.bind(this);
    }

    connectedCallback() {
      this.list = this.querySelector("[data-cart-cross-sell-list]");
      if (!this.list) return;

      // Only fetch once the drawer is actually open so idle page loads don't prefetch.
      if (this.isVisible()) {
        this.load();
      } else {
        this.watchForOpen();
      }
    }

    disconnectedCallback() {
      if (this.drawerObserver) this.drawerObserver.disconnect();
      if (this.flickity) {
        try {
          this.flickity.destroy();
        } catch (e) {
          /* noop */
        }
        this.flickity = null;
      }
    }

    isVisible() {
      var drawer = this.closest("cart-drawer");
      if (drawer) return drawer.classList.contains("active");
      return this.offsetParent !== null;
    }

    watchForOpen() {
      var drawer = this.closest("cart-drawer");
      if (!drawer) {
        var io = new IntersectionObserver(
          function (entries, obs) {
            if (entries[0].isIntersecting) {
              obs.disconnect();
              this.load();
            }
          }.bind(this),
          { rootMargin: "0px 0px 200px 0px" }
        );
        io.observe(this);
        return;
      }
      this.drawerObserver = new MutationObserver(this.onCartDrawerMutation);
      this.drawerObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    onCartDrawerMutation() {
      if (!this.isVisible()) return;
      if (this.drawerObserver) this.drawerObserver.disconnect();
      this.load();
    }

    load() {
      if (this.loaded) {
        this.initSlider();
        return;
      }
      this.loaded = true;

      var url = this.dataset.url;
      if (!url) return;

      fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(
          function (text) {
            var parsed = new DOMParser().parseFromString(text, "text/html");
            var freshList = parsed.querySelector("[data-cart-cross-sell-list]");
            if (freshList) this.list.innerHTML = freshList.innerHTML;

            if (!this.list.querySelector(".cart-cross-sell__item")) {
              // Nothing left after filtering — hide the heading + scrollbar too.
              this.hidden = true;
              return;
            }

            this.classList.add("cart-cross-sell--loaded");
            this.initSlider();
          }.bind(this)
        )
        .catch(function (error) {
          console.error("cart-cross-sell:", error);
        });
    }

    initSlider() {
      if (typeof Flickity === "undefined") {
        if (this.flickityRetries++ < 20)
          setTimeout(this.initSlider.bind(this), 100);
        return;
      }
      // Flickity needs a laid-out (visible) element to measure cell widths.
      if (this.offsetParent === null) return;

      if (!this.flickity) {
        this.flickity = new Flickity(this.list, FLICKITY_OPTIONS);
        this.setupScrollbar();
        this.focusOpenedCard();
      } else {
        var currentX = this.flickity.x;
        this.flickity.resize();
        this.flickity.x = currentX;
        this.flickity.positionSlider();
      }
    }

    // Keep the card whose size dropdown was just opened fully in view.
    focusOpenedCard() {
      var self = this;
      this.list.addEventListener(
        "click",
        function (event) {
          var trigger = event.target.closest(
            ".product-card-variant-picker__trigger"
          );
          if (!trigger || !self.list.contains(trigger)) return;
          var cell = trigger.closest(".cart-cross-sell__item");
          if (!cell || !self.flickity) return;

          setTimeout(function () {
            var currentX = self.flickity.x;
            self.flickity.resize();
            self.flickity.x = currentX;
            self.flickity.positionSlider();
            self.moveCellIntoView(cell);
          }, 0);
        },
        true
      );
    }

    moveCellIntoView(cell) {
      var viewport = this.list.querySelector(".flickity-viewport") || this.list;
      var cellRect = cell.getBoundingClientRect();
      var viewportRect = viewport.getBoundingClientRect();
      var visibleLeft = Math.max(viewportRect.left, 0);
      var visibleRight = Math.min(
        viewportRect.right,
        document.documentElement.clientWidth
      );
      var nextX = this.flickity.x;

      if (cellRect.left < visibleLeft) {
        nextX += visibleLeft - cellRect.left;
      } else if (cellRect.right > visibleRight) {
        nextX -= cellRect.right - visibleRight;
      }
      if (nextX !== this.flickity.x) {
        this.flickity.x = nextX;
        this.flickity.velocity = 0;
        this.flickity.positionSlider();
      }
    }

    // Draggable scrollbar synced to Flickity so the slider has a visible start/end indicator.
    setupScrollbar() {
      var flickity = this.flickity;
      var list = this.list;
      var bar = this.querySelector(".cart-cross-sell__scrollbar");
      var thumb = bar && bar.querySelector(".cart-cross-sell__scrollbar-thumb");
      if (!bar || !thumb) return;

      function viewportWidth() {
        return flickity.size ? flickity.size.innerWidth : list.clientWidth;
      }

      function sizeThumb() {
        var total = flickity.slideableWidth || list.scrollWidth;
        var ratio = total > 0 ? Math.min(1, viewportWidth() / total) : 1;
        thumb.style.width = ratio * 100 + "%";
        bar.style.display = ratio >= 1 ? "none" : "";
      }

      function positionThumb(progress) {
        var p = Math.max(0, Math.min(1, progress));
        var travel = bar.clientWidth - thumb.clientWidth;
        thumb.style.transform = "translateX(" + p * travel + "px)";
      }

      sizeThumb();
      positionThumb(flickity.progress || 0);
      flickity.on("scroll", positionThumb);
      flickity.on("resize", function () {
        sizeThumb();
        positionThumb(flickity.progress || 0);
      });

      var dragging = false;
      function thumbToProgress(clientX) {
        var rect = bar.getBoundingClientRect();
        var travel = bar.clientWidth - thumb.clientWidth;
        if (travel <= 0) return 0;
        return (clientX - rect.left - thumb.clientWidth / 2) / travel;
      }
      function onMove(e) {
        if (!dragging) return;
        var x = e.touches ? e.touches[0].clientX : e.clientX;
        var p = Math.max(0, Math.min(1, thumbToProgress(x)));
        var maxX = -(flickity.slideableWidth - viewportWidth());
        flickity.x = p * maxX;
        flickity.velocity = 0;
        flickity.positionSlider();
        positionThumb(p);
      }
      function endDrag() {
        dragging = false;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("touchmove", onMove);
      }
      thumb.addEventListener("mousedown", function (e) {
        dragging = true;
        e.preventDefault();
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", endDrag);
      });
      thumb.addEventListener(
        "touchstart",
        function () {
          dragging = true;
          document.addEventListener("touchmove", onMove, { passive: true });
          document.addEventListener("touchend", endDrag);
        },
        { passive: true }
      );
    }
  }

  if (!customElements.get("cart-cross-sell")) {
    customElements.define("cart-cross-sell", CartCrossSell);
  }
})();
